import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { TrackingEvents } from '../../../../../../../analytics/events';
import { trackCustomEvent } from '../../../../../../../analytics/Mixpanel';
import { useCreateTranscript } from '../../../../../../../api/mutations/useCreateTranscript';
import { capitalizeFirstLetter } from '../../../../../../../helpers/filterNames';
import {
  RecordingDetails,
  RecordingDetailsModal,
} from '../../../../../../../screens/Chronos/transcribe/components/RecordingDetailsModal';
import { Icon } from '../../../../../../components/icon';
import { Button } from '../../../../../../components/shadcn/button';
import { cn } from '../../../../../../lib/utils';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetAllTranscripts } from '@/api/queries/useGetTranscripts';
import { TranscriptWithCount } from '@/api/types/backend-dto-types';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/new/components/shadcn/table';
import { TableFooter } from '@/new/components/table-footer';

const fallbackTableData: TranscriptWithCount[] = [];
const columnHelper = createColumnHelper<TranscriptWithCount>();

function formatSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes ? minutes + 'm ' : ''}${remainingSeconds}s`;
}

function StatusChip({ status }: { status: string }) {
  let colour = 'bg-gray-400';

  switch (status) {
    case 'completed':
      colour = 'bg-green-400';
      break;

    case 'recording':
      colour = 'bg-red-400';
      break;

    default:
      break;
  }

  return (
    <div className="flex w-fit items-center gap-1.5 rounded-md border bg-gray-50 px-1.5 py-0.5 text-xs text-gray-800">
      <div className={cn(colour, 'size-1.5 rounded-full')}></div>
      {capitalizeFirstLetter(status)}
    </div>
  );
}

export function Realtime() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  document.title = 'Realtime • wexler.ai';

  const { i18n } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => <span>Transcript Name</span>,
        cell: (info) => (
          <Link
            to={info.row.original.id}
            onClick={(e) => e.stopPropagation()}
            className="line-clamp-1 min-w-48 break-all"
          >
            {info.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor('status', {
        header: () => <span>Status</span>,
        cell: (info) => <StatusChip status={info.getValue()} />,
      }),
      columnHelper.accessor('durationSeconds', {
        header: () => <span>Duration</span>,
        cell: (info) => {
          const seconds = info.getValue();
          return <span className="whitespace-nowrap">{seconds ? formatSeconds(seconds) : '-'}</span>;
        },
      }),
      columnHelper.accessor('inconsistencyCount', {
        header: () => <span>Inconsistencies</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Created Date</span>,
        cell: (info) => (
          <span className="whitespace-nowrap">
            {new Date(info.getValue()).toLocaleString(i18n.language, {
              dateStyle: 'medium',
              timeStyle: 'medium',
            })}
          </span>
        ),
      }),
    ],
    [i18n],
  );

  const { data: caseData } = useGetCase(matterId);
  const { data: transcripts, isLoading } = useGetAllTranscripts(matterId);

  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: transcripts ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      globalFilter,
    },
  });

  const [showRecordingDetailsModal, setShowNewRecordingModal] = useState(false);

  const handleNewRecordingClick = () => {
    trackCustomEvent(TrackingEvents.ClickNewRecordingButton);
    setShowNewRecordingModal(true);
  };

  const createTranscriptMutation = useCreateTranscript();

  const createNewTranscript = async (details: RecordingDetails) => {
    const newTranscript = await createTranscriptMutation.mutateAsync({
      caseId: matterId,
      name: details.name,
      objective: details.objective,
      deponent: details.deponent,
      otherSpeakers: details.otherSpeakers,
      phraseList: details.phraseList,
    });

    setShowNewRecordingModal(false);

    navigate(newTranscript.id);
  };

  return (
    <>
      <Breadcrumb
        items={
          caseData
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${caseData.id}`,
                  label: caseData.name,
                  active: false,
                },
                {
                  href: `/matters/${caseData.id}/subsets/${subsetId}`,
                  label: '<subset name>',
                  active: false,
                  iconClass: 'lucide--group',
                },
                {
                  href: `/matters/${caseData.id}/subsets/${subsetId}/realtime`,
                  label: 'Realtime',
                  active: true,
                },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle text="Realtime" />
        </PageHeader>

        <div className="my-6 flex items-center gap-2">
          <SearchInput
            placeholder="Search transcripts..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm grow"
          />

          <Button variant="primary" onClick={handleNewRecordingClick}>
            <Icon className="lucide--circle-fading-plus" />
            Start a New Recording
          </Button>
        </div>

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} onClick={() => navigate(row.original.id)} className="cursor-pointer">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!isLoading && <TableFooter table={table} plural="transcripts" />}
      </PageContainer>

      <RecordingDetailsModal
        isOpen={showRecordingDetailsModal}
        onClose={() => setShowNewRecordingModal(false)}
        onConfirm={createNewTranscript}
        isLoading={createTranscriptMutation.isLoading}
      />
    </>
  );
}
